import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from "@mui/material";
import apiClient from "../../utils/apiClient";
import escapeHtml from "lodash/escape";
import Swal from "../../utils/swal";
import { showSuccessToast, showErrorToast } from "../../utils/swalToast";
import { useNavigate } from "react-router-dom";
import backCard from "../../images/back_card.png";
import frontCard from "../../images/front_card.png";
import logo from "../../images/ikdki.png";
import RowActionsMenu from "../custom/RowActionsMenu";
import CustomDataGrid from "../custom/CustomDataGrid";
import CustomButton from "../custom/CustomButton";
import CustomDatePicker from "../custom/CustomDatePicker";
import EditMemberModal from "./EditMemberModal";
import { useUser, useUserActions } from "../../pages/UserContext";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/id";

dayjs.extend(customParseFormat);
dayjs.locale("id");

const parseExpiredDate = (value) => {
  if (!value) return null;

  const formats = [
    "DD MMMM YYYY",
    "DD MMM YYYY",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD",
  ];

  for (const format of formats) {
    const parsed = dayjs(value, format, "id", true);
    if (parsed.isValid()) return parsed;
  }

  return null;
};

const getMembershipStatus = (value) => {
  const expiredDate = parseExpiredDate(value);
  if (!expiredDate) return "invalid";
  return expiredDate.isBefore(dayjs().startOf("day")) ? "inactive" : "active";
};

const STATUS_FILTER_OPTIONS = [
  { key: "all", label: "Semua", color: "default" },
  { key: "active", label: "Keanggotaan Aktif", color: "success" },
  { key: "inactive", label: "Keanggotaan Tidak Aktif", color: "error" },
  { key: "invalid", label: "Tanggal Tidak Valid", color: "warning" },
];

// Doubles as the filter control — clicking a chip filters the table to that
// status, same colors as the badges shown in the "Masa Keanggotaan" column.
const MembershipLegend = ({ value, onChange }) => (
  <Grid
    container
    spacing={1}
    alignItems="center"
    sx={{ mb: 1 }}
  >
    {STATUS_FILTER_OPTIONS.map((opt) => (
      <Grid item key={opt.key}>
        <Chip
          size="small"
          color={opt.color}
          variant={value === opt.key ? "filled" : "outlined"}
          label={opt.label}
          onClick={() => onChange(opt.key)}
          sx={{ p: 2, cursor: "pointer" }}
        />
      </Grid>
    ))}
  </Grid>
);


export default function MemberData() {
  const userDetails = useUser();
  const { refetchUser } = useUserActions();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalLecturer, setTotalLecturer] = useState(0);
  const [totalAnggotaKehormatan, setTotalAnggotaKehormatan] = useState(0);
  const [totalTendik, setTotalTendik] = useState(0);
  const [totalMahasiswa, setTotalMahasiswa] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const [openRenewModal, setOpenRenewModal] = useState(false);
  const [renewDate, setRenewDate] = useState(null);
  const [selectedUuid, setSelectedUuid] = useState(null);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editUserId, setEditUserId] = useState(null);

  const handleOpenEdit = (userId) => {
    setEditUserId(userId);
    setOpenEditModal(true);
  };

  const handleEditSuccess = () => {
    setOpenEditModal(false);
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await apiClient.get("/users?member=1");

        setRows(
          response.data.data.map((user, index) => ({
            userId: user.uuid,
            cardNum: user.card_num,
            yearJoin: user.year_join,
            id: index + 1 + (page - 1) * 10,
            namaAnggota: user.nama_lengkap,
            universitas: user.institusi_asal,
            provinsi: user.provinsi_institusi,
            role: user.role,
            expiredCardAt: user.expired_card_at,
          }))
        );
      } catch (err) {
        console.error("Error fetching member", err);
      } finally {
        setLoading(false);
      }
    };


    const fetchLecturer = async () => {
      try {
        const response = await apiClient.get("/users/role", {
          params: { role: "Dosen" },
        });
        setTotalLecturer(response.data.data);
      } catch (error) {
        console.error("Error fetching data from API:", error);
      }
    };

    const fetchAnggotaKehormatan = async () => {
      try {
        const response = await apiClient.get("/users/role", {
          params: { role: "Anggota Kehormatan" },
        });
        setTotalAnggotaKehormatan(response.data.data);
      } catch (error) {
        console.error("Error fetching data from API:", error);
      }
    };

    const fetchTendik = async () => {
      try {
        const response = await apiClient.get("/users/role", {
          params: { role: "Tenaga Pendidik / Tendik" },
        });
        setTotalTendik(response.data.data);
      } catch (error) {
        console.error("Error fetching data from API:", error);
      }
    };

    const fetchMahasiswa = async () => {
      try {
        const response = await apiClient.get("/users/role", {
          params: { role: "Mahasiswa/i" },
        });
        setTotalMahasiswa(response.data.data);
      } catch (error) {
        console.error("Error fetching data from API:", error);
      }
    };

    fetchLecturer();
    fetchMahasiswa();
    fetchTendik();
    fetchAnggotaKehormatan();
    fetchMembers();

  }, [refresh, page]);

  const generateHTML = (divContents) => {
    return `
      <html>
        <head>
          <style>
            #logo {
              left: 42px !important;
            }
            #cardMemberName {
              left: 5px !important;
              top: 262px !important;
              color: white !important;
              // top: 200px !important;
            }
            #cardMemberNumber {
              left: 5px !important;
              top: 285px !important;
              color: white !important;
            }
            #cardYearExpired {
              left: 210px !important;
              top: 78px !important;
              color: white !important;
            }
          </style>
        </head>
        <body width='100%' style="text-align">
          ${divContents}
        </body>
      </html>
    `;
  };

  const downloadCanvas = async (canvasId) => {
    var divContents = document.getElementById(canvasId).innerHTML;
    var a = window.open("", "", "height=500, width=500");
    a.document.write(generateHTML(divContents));
    a.document.close();
    setTimeout(() => {
      a.print();
    }, 4500);
  };

  const printCard = async (
    namaAnggota,
    nomorAnggota,
    tahunPertamaMenjadiMember,
    tahunKadaluarsaKartu
  ) => {
    Swal.fire({
      html: `
        <div style="position: relative;" id="cardCanvas">
          <div id="cardMemberName" 
              style="position: absolute; top: 258px; left: 25px; color: white; font-size: 10px; text-align: center; width: 200px; 
                      word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
            <b>${namaAnggota}</b>
          </div>
          <div id="cardMemberNumber" style="position: absolute; top: 275px; left: 25px; color: white; font-size: 12px; text-align: center; width: 200px;"><b>${nomorAnggota}</b></div>
          <div id="cardYearExpired" style="position: absolute; top: 72px; left: 227px; color: white; font-size: 13px; text-align: center; width: 200px;">
            <b>01 Jan ${tahunPertamaMenjadiMember} s/d ${tahunKadaluarsaKartu}</b>
          </div>
          <img id="frontCardImage" src="${frontCard}" alt="Front Card" style="width: 200px; height: auto;">
          <img id="logo" src="${logo}" alt="Logo" style="position: absolute; top: 120px; left: 58px; width: 130px; height: auto;">
          <img id="backCardImage" src="${backCard}" alt="Back Card" style="width: 200px; height: auto;">
          <br>
        </div>
      `,
      showCloseButton: true,
      confirmButtonText: "Print",
    }).then((res) => {
      if (res.isConfirmed === true) {
        downloadCanvas("cardCanvas");
      }
    });
  };

  const renewMembership = (uuid, namaAnggota, expiredCardAt) => {
    Swal.fire({
      icon: "warning",
      html: `
      Renew Membership<br/>
      <span style="color: #d32f2f; font-weight: 600;">
        ${namaAnggota}
      </span> ?    `,
      showCancelButton: true,
      confirmButtonText: "Perbaharui",
      cancelButtonText: "Tidak",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedUuid(uuid);
        setOpenRenewModal(true);
        const renewDate = `${dayjs().year()}-12-31 00:00:00`;
        console.log(renewDate)
        setRenewDate(
          dayjs(renewDate, "YYYY-MM-DD HH:mm:ss")
        );
      }
    });
  };

  const handleSubmitRenew = async () => {
    try {
      await apiClient.post("/users/member/renew-membership", {
        uuid: selectedUuid,
        renew_until: renewDate,
      });

      showSuccessToast("Masa keanggotaan berhasil diperbaharui");

      setOpenRenewModal(false);
      setRenewDate(null);

      setRefresh(prev => !prev);

    } catch (error) {
      showErrorToast(error.response?.data?.msg || "Terjadi kesalahan");
    }
  };

  const handleImpersonate = (uuid, namaAnggota) => {
    Swal.fire({
      title: "Login As",
      html: `Apakah anda ingin masuk sebagai <b>${escapeHtml(namaAnggota)}</b>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Login As",
      cancelButtonText: "Batal",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await apiClient.post(`/impersonate/${uuid}`, {});
        // The backend just swapped the session cookies to the target user's;
        // refetching is what picks that up on the frontend.
        await refetchUser();
        navigate("/member-card");
      } catch (error) {
        showErrorToast(error.response?.data?.msg || "Gagal melakukan impersonasi");
      }
    });
  };

  const handleDeleteUser = (uuid, namaAnggota) => {
    Swal.fire({
      title: "Hapus Anggota",
      html: `Apakah anda yakin ingin menghapus <b>${escapeHtml(namaAnggota)}</b>? Data akan diarsipkan dan akun ini tidak akan bisa login lagi.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await apiClient.delete(`/users/${uuid}`);
        showSuccessToast("Anggota berhasil dihapus");
        setRefresh((prev) => !prev);
      } catch (error) {
        showErrorToast(error.response?.data?.msg || "Gagal menghapus anggota");
      }
    });
  };

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((row) => getMembershipStatus(row.expiredCardAt) === statusFilter);
  }, [rows, statusFilter]);

  const columns = [
    { field: "id", headerName: "No.", flex: 1 },
    { field: "namaAnggota", headerName: "Nama Anggota", width: 200, cellClassName: "center-cell" },
    { field: "universitas", headerName: "Universitas", width: 150 },
    { field: "provinsi", headerName: "Provinsi", flex: 1 },
    { field: "cardNum", headerName: "No. Anggota", width: 100 },
    { field: "yearJoin", headerName: "Tahun Masuk", width: 100 },
    {
      field: "expiredCardAt",
      headerName: "Masa Keanggotaan",
      width: 200,
      renderCell: (params) => {
        const expiredDate = parseExpiredDate(params.value);
        const status = getMembershipStatus(params.value);

        if (status === "invalid") {
          return (
            <Chip
              label="Tanggal tidak valid"
              color="warning"
              size="small"
            />
          );
        }

        return (
          <Chip
            label={expiredDate.format("D MMMM YYYY")}
            color={status === "inactive" ? "error" : "success"}
            size="small"
            sx={{
              p:1.5,
              fontWeight: 600,
              maxWidth: "100%",
              "& .MuiChip-label": {
                whiteSpace: "normal",
                overflow: "visible",
                textOverflow: "unset",
              },
            }}
          />
        );
      }
    },
    { field: "role", headerName: "Role", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      headerAlign: "justify",
      width: 120,
      renderCell: (cellValues) => (
        <RowActionsMenu
          actions={[
            {
              label: "Edit User",
              onClick: () => handleOpenEdit(cellValues.row.userId),
            },
            {
              label: "Renew Membership",
              onClick: () =>
                renewMembership(
                  cellValues.row.userId,
                  cellValues.row.namaAnggota,
                  cellValues.row.expiredCardAt
                ),
            },
            ...(userDetails?.accessRole === "super_admin" &&
            cellValues.row.userId !== userDetails.uuid
              ? [
                  {
                    label: "Login As",
                    onClick: () =>
                      handleImpersonate(cellValues.row.userId, cellValues.row.namaAnggota),
                  },
                  {
                    label: "Hapus",
                    onClick: () =>
                      handleDeleteUser(cellValues.row.userId, cellValues.row.namaAnggota),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div style={{ height: "100%", width: "100%" }}>
      {/* Bagian Card Statistik */}
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" align="center">
                Jumlah Dosen
              </Typography>
              <Typography variant="h5" align="center" fontWeight="bold">
                {totalLecturer}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" align="center">
                Anggota Kehormatan
              </Typography>
              <Typography variant="h5" align="center" fontWeight="bold">
                {totalAnggotaKehormatan}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" align="center">
                Tenaga Pendidik
              </Typography>
              <Typography variant="h5" align="center" fontWeight="bold">
                {totalTendik}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" align="center">
                Mahasiswa/i
              </Typography>
              <Typography variant="h5" align="center" fontWeight="bold">
                {totalMahasiswa}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <MembershipLegend value={statusFilter} onChange={setStatusFilter} />
      <CustomDataGrid
        rows={filteredRows}
        columns={columns}
        page={page}
        setPage={setPage}
        loading={loading}
      />

      <Dialog
        open={openRenewModal}
        onClose={() => setOpenRenewModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renew Membership</DialogTitle>

        <DialogContent
          sx={{
            pt: 3,
            pb: 3,
            overflow: "visible",
          }}
        >
          <CustomDatePicker
            label="Tanggal Berakhir Keanggotaan"
            value={renewDate}
            onChange={(date) => setRenewDate(date)}
            minDate={dayjs().startOf("day")}
          />

        </DialogContent>

        <DialogActions sx={{ padding: "15px 40px", gap: 1 }}>
          <CustomButton
            text="Batal"
            baseColor="#A92027"
            hoverColor="#8C1016"
            onClick={() => setOpenRenewModal(false)}
          />

          <CustomButton
            text="Simpan"
            baseColor="#003158"
            hoverColor="#00213A"
            onClick={handleSubmitRenew}
            disabled={!renewDate}
          />
        </DialogActions>
      </Dialog>

      <EditMemberModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        userId={editUserId}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
}
